import React, { Component } from 'react';
import update from 'immutability-helper';

import Login from './components/Login';
import Wait from './components/Wait';
import Quiz from './components/Quiz';
import * as quizInfraService from './services/quizInfraService';

export default class App extends Component {
    constructor(props) {
        super(props);

        this.state = {
            user: {
                token: null,
                name: null
            },
            clock: {
                countdown: null
            },
            status: 'connecting',
            details: null,
            round: null,
            question: null,
            answer: null
        };

        this.unmounting = false;

        this.handleLetsPlay = this.handleLetsPlay.bind(this);
        this.handleOpen = this.handleOpen.bind(this);
        this.handleMessage = this.handleMessage.bind(this);
        this.handleError = this.handleError.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleGuess = this.handleGuess.bind(this);
        this.handleHalf = this.handleHalf.bind(this);
        this.handleHeart = this.handleHeart.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.load = this.load.bind(this);
    }

    componentDidMount() {
        // To reset user uncomment and run app
        // localStorage.removeItem('user')
        this.load();
    }

    componentWillUnmount() {
        this.unmounting = true;
        quizInfraService.logout();
    }

    handleLetsPlay(name, birthYear, genre) {
        this.signup(name, birthYear, genre)
            .then(() => {
                this.setState({
                    user: update(this.state.user, {
                        name: { $set: name }
                    })
                });
            })
            .catch(error => {
                let message = error.message;
                switch (message) {
                    case 'NAME_EXISTS':
                        message = 'Name already exists. Please choose another name';
                        break;
                    case 'INVALID_BIRTH_YEAR':
                        message = 'Please check your birth year';
                        break;
                }
                alert('Something went wrong: ' + message);
            });
    }

    load() {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            return this.setState({ status: 'newUser' });
        }
        
        const user = JSON.parse(storedUser);
        if (!user.birthYear || !user.genre) {
            return this.setState({ status: 'newUser' });
        }
        
        this.setState({
            user: update(this.state.user, {
                name: { $set: user.name }
            })
        });
        
        quizInfraService.user(user.token)
            .then(user => this.login(user.token))
            .catch(error => {
                if (error.message !== 'NOT_FOUND') {
                    throw error;
                }
                return this.signup(user.name, user.birthYear, user.genre);
            })
            .catch(error => {
                if (error.message === 'NAME_EXISTS') {
                    alert('Something went wrong: Name already exists. Please choose another name');
                    return this.setState({ status: 'newUser' });
                }
                alert('Something went wrong: ' + error.message);
            });
    }

    signup(name, birthYear, genre) {
        return quizInfraService.signup(name, birthYear, genre)
            .then(user => {
                return Promise.resolve(localStorage.setItem('user', JSON.stringify(user)))
                    .then(() => this.login(user.token));
            });
    }

    login(token) {
        quizInfraService.login(token, this.handleOpen, this.handleMessage, this.handleError, this.handleClose);
    }

    handleOpen() {
        this.setState({ status: 'loading' });
    }

    handleError(error) {
        console.log(error);
    }

    handleClose(event) {
        if (this.unmounting) {
            return;
        }
        this.load();
    }

    handleMessage({ type, content }) {
        switch (type) {
            case 'state':
            case 'reset':
                this.setState(content);
                break;
            case 'clock':
                this.setState({ clock: content });
                break;
            case 'details':
                this.setState({ details: content });
                break;
            case 'quiz':
                this.setState({ status: 'waiting' });
                break;
            case 'start':
                this.setState({ status: 'start', details: content, round: null, question: null, answer: null });
                break;
            case 'round':
                this.setState({ status: 'round', round: content });
                break;
            case 'getReady':
                this.setState({ status: 'getReady', question: content.question });
                break;
            case 'question':
                this.setState({ status: 'question', question: content, clock: { countdown: content.countdown }, answer: null });
                break;
            case 'half':
                this.setState({
                    status: 'question', question: update(this.state.question, {
                        options: { $set: content }
                    })
                });
                break;
            case 'answer':
                this.setState({ status: 'answer', answer: content });
                break;
            case 'summary':
                this.setState({ status: 'summary' });
                break;
            case 'gameover':
                this.setState({ status: 'gameover', clock: { countdown: null } });
                break;
            case 'totalPlayers':
                this.setState({ details: update(this.state.details, { totalPlayers: { $set: content } }) });
                break;
        }
    }

    handleGuess(optionIndex) {
        if (this.state.status !== 'question' || (this.state.answer && this.state.answer.userOptionIndex !== undefined)) {
            return;
        }
        quizInfraService.guess(optionIndex);
        this.setState({
            answer: {
                userOptionIndex: optionIndex
            }
        });
    }

    handleHalf() {
        if (this.state.status !== 'question' || (this.state.answer && this.state.answer.userOptionIndex !== undefined) || this.state.question.half) {
            return;
        }
        quizInfraService.half();
        this.setState({
            question: update(this.state.question, {
                half: { $set: true }
            })
        });
    }

    handleHeart() {
        if ((this.state.status !== 'question' && this.state.status !== 'answer') || this.state.question.heart) {
            return;
        }
        quizInfraService.heart();
        this.setState({
            question: update(this.state.question, {
                heart: { $set: true }
            })
        });
    }

    handleBack() {
        this.setState({ status: 'noQuiz' });
    }

    render() {
        const appContainerStyle = {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%'
        };

        return (
            <div className="app-container">
                {this.renderContent()}
            </div>
        );
    }

    renderContent() {
        switch (this.state.status) {
            case 'newUser':
                return <Login onLetsPlay={this.handleLetsPlay} />;
            case 'connecting':
            case 'loading':
            case 'noQuiz':
            case 'waiting':
                return <Wait 
                    user={this.state.user} 
                    status={this.state.status} 
                    clock={this.state.clock} 
                />;
            default:
                return <Quiz 
                    user={this.state.user} 
                    status={this.state.status} 
                    clock={this.state.clock}
                    details={this.state.details} 
                    round={this.state.round} 
                    question={this.state.question} 
                    answer={this.state.answer}
                    onGuess={this.handleGuess} 
                    onHalf={this.handleHalf} 
                    onHeart={this.handleHeart} 
                    onBack={this.handleBack} 
                />;
        }
    }
}
